import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import { Loader2, ArrowLeft, ShieldOff, ShieldCheck, KeyRound, Activity, BarChart3, Search, Calendar, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { toast } from 'sonner';

function Section({ title, items, render, testid }) {
  return (
    <div className="fab-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-bold text-fab-text text-[14px]">{title}</div>
        <div className="text-[11px] text-fab-text-muted font-mono">{items.length} recent</div>
      </div>
      {items.length === 0 ? (
        <div className="text-[12px] text-fab-text-muted italic py-2">None yet.</div>
      ) : (
        <div className="space-y-1.5 text-[12.5px]">{items.map(render)}</div>
      )}
    </div>
  );
}

export default function AdminVendorDetail() {
  const { vid } = useParams();
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/vendors/${vid}`),
      api.get('/admin/activity-log', { params: { vendor_id: vid, limit: 50 } }),
    ]).then(([d, l]) => { setData(d.data); setLogs(l.data?.items || []); }).finally(() => setLoading(false));
  };
  useEffect(load, [vid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>;
  if (!data) return <div className="fab-card p-6 text-fab-text-muted">Vendor not found.</div>;
  const v = data.vendor;
  const counts = data.counts || {};
  const recent = data.recent || {};

  const suspend = async () => {
    const reason = window.prompt('Reason (optional)') || '';
    try { await api.post(`/admin/vendors/${vid}/suspend`, { reason }); toast.success('Suspended'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const activate = async () => {
    try { await api.post(`/admin/vendors/${vid}/activate`, {}); toast.success('Reactivated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const reset = async () => {
    const p = window.prompt(`New password (min 8 chars):`);
    if (!p) return;
    try { await api.post(`/admin/vendors/${vid}/reset-password`, { new_password: p }); toast.success('Password reset'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-vendor-detail">
      <Link to="/dashboard/admin/vendors" className="text-[12px] font-semibold text-fab-text-secondary hover:text-fab-primary inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All vendors</Link>

      <div className="fab-card p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl font-bold text-fab-text">{v.business_name || '—'}</h2>
            <div className="text-[13px] text-fab-text-muted">{v.name} · {v.email}</div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-mono font-bold">
              <span className="px-2 py-0.5 rounded border border-fab-primary-lighter">PLAN {v.plan_tier || 'FREE_TRIAL'}</span>
              {v.is_admin && <span className="px-2 py-0.5 rounded bg-fab-primary text-white">ADMIN</span>}
              {v.suspended && <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-300">SUSPENDED</span>}
              {v.ai_globally_paused && <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-300">AI PAUSED</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {v.suspended
              ? <button data-testid="detail-activate" onClick={activate} className="text-[12px] font-semibold px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Activate</button>
              : <button data-testid="detail-suspend" onClick={suspend} className="text-[12px] font-semibold px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"><ShieldOff className="w-3 h-3" /> Suspend</button>}
            <button data-testid="detail-reset" onClick={reset} className="text-[12px] font-semibold px-3 py-1.5 rounded border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Reset password</button>
            <Link to={`/dashboard/admin/sellers/${vid}/matrix`} data-testid="detail-matrix" className="text-[12px] font-semibold px-3 py-1.5 rounded border border-fab-primary bg-fab-primary text-white hover:opacity-90 inline-flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Matrix</Link>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-4 text-center">
          {[
            ['Products', counts.products],
            ['Customers', counts.customers],
            ['Groups', counts.groups],
            ['Broadcasts', counts.broadcasts],
            ['Conversations', counts.conversations],
            ['Messages', counts.messages],
          ].map(([l, n]) => (
            <div key={l} className="border border-fab-primary-lighter rounded-lg p-2" data-testid={`count-${l.toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold">{l}</div>
              <div className="font-display font-bold text-fab-text text-lg">{n ?? 0}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="fab-card p-4" data-testid="vendor-wa-section">
        <div className="font-display font-bold text-fab-text text-[14px] mb-2">WhatsApp Numbers</div>
        {(data.whatsapp_numbers || []).length === 0 ? (
          <div className="text-[12px] text-fab-text-muted italic">No WhatsApp numbers connected.</div>
        ) : (
          <div className="space-y-1.5">
            {data.whatsapp_numbers.map((n) => (
              <div key={n.id} className="flex items-center justify-between text-[12.5px] border-b border-fab-primary-lighter pb-1.5 last:border-0">
                <div>
                  <span className="font-mono font-semibold">{n.phone_number || '—'}</span>
                  <span className="text-fab-text-muted ml-2">{n.display_name}</span>
                </div>
                <span className="font-mono text-[10.5px] font-bold">{n.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <AiCostLog vid={vid} />

      <div className="grid sm:grid-cols-2 gap-3">
        <Section testid="recent-groups" title="Recent Groups" items={recent.groups || []} render={(g) => (
          <div key={g.id} className="flex justify-between border-b border-fab-primary-lighter pb-1 last:border-0">
            <div>{g.group_name}</div>
            <div className="font-mono text-[11px]">{(g.members || []).length} members</div>
          </div>
        )} />
        <Section testid="recent-broadcasts" title="Recent Broadcasts" items={recent.broadcasts || []} render={(b) => (
          <div key={b.id} className="flex justify-between border-b border-fab-primary-lighter pb-1 last:border-0">
            <div className="truncate">{b.message?.slice(0, 50) || b.id}</div>
            <div className="font-mono text-[11px]">{b.status}</div>
          </div>
        )} />
      </div>

      <div className="fab-card p-4" data-testid="vendor-activity-log">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display font-bold text-fab-text text-[14px] flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Activity Log</div>
          <div className="text-[11px] text-fab-text-muted font-mono">{logs.length} entries</div>
        </div>
        {logs.length === 0 ? (
          <div className="text-[12px] text-fab-text-muted italic">No activity yet.</div>
        ) : (
          <div className="space-y-1 text-[12px] max-h-60 overflow-y-auto">
            {logs.map((l) => (
              <div key={l.id} className="flex justify-between border-b border-fab-primary-lighter pb-1 last:border-0">
                <span className="font-mono">{l.action}</span>
                <span className="text-fab-text-muted">{l.timestamp ? new Date(l.timestamp).toLocaleString() : '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PROC_STYLE = {
  ai_call: 'bg-fab-primary/10 text-fab-primary border-fab-primary/20',
  no_ai_call: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  unknown: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};
const PROC_LABEL = { ai_call: 'AI Call', no_ai_call: 'No AI Call', unknown: 'Unknown' };
const STATUS_STYLE = {
  tracked: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  unverified: 'bg-amber-50 text-amber-700 border-amber-200',
};

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const val = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    const label = dt.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    out.push([val, label]);
  }
  return out;
}

function AiCostLog({ vid }) {
  const months = monthOptions();
  const [month, setMonth] = useState(months[0][0]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: 10 };
    if (start && end) { params.start = start; params.end = end; }
    else { params.month = month; }
    if (q.trim()) params.q = q.trim();
    api.get(`/admin/sellers/${vid}/message-costs`, { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [vid, month, start, end, q, page]);

  useEffect(() => { load(); }, [vid, month, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => { setPage(1); load(); };
  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return (<><div>{d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
        <div className="text-fab-text-muted">{d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div></>);
    } catch { return iso; }
  };
  const fmtCost = (r) => (r.cost_inr === null || r.cost_inr === undefined)
    ? <span className="text-amber-600">—</span>
    : `₹${Number(r.cost_inr).toFixed(2)}`;
  const from = data ? (data.page - 1) * data.page_size + 1 : 0;
  const to = data ? Math.min(data.page * data.page_size, totals.total_messages || 0) : 0;

  return (
    <div className="fab-card p-5" data-testid="ai-cost-log">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <div className="font-display font-bold text-fab-text text-[17px] flex items-center gap-1.5">
            AI Usage &amp; Message Cost Log
            <Info className="w-3.5 h-3.5 text-fab-text-muted" />
          </div>
          <div className="text-[12px] text-fab-text-muted mt-0.5">
            Every buyer message and AI call for this seller account. Includes zero-AI messages. Costs shown in INR.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Calendar className="w-3.5 h-3.5 text-fab-text-muted absolute left-2 top-1/2 -translate-y-1/2" />
            <select
              data-testid="cost-month-select"
              value={start && end ? '' : month}
              onChange={(e) => { setStart(''); setEnd(''); setMonth(e.target.value); setPage(1); }}
              className="text-[12px] font-semibold pl-7 pr-2 py-1.5 rounded border border-fab-primary-lighter bg-white">
              {start && end && <option value="">Custom range</option>}
              {months.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <input type="date" data-testid="cost-start-date" value={start}
            onChange={(e) => setStart(e.target.value)}
            className="text-[12px] px-2 py-1.5 rounded border border-fab-primary-lighter" />
          <span className="text-fab-text-muted">→</span>
          <input type="date" data-testid="cost-end-date" value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="text-[12px] px-2 py-1.5 rounded border border-fab-primary-lighter" />
          <button data-testid="cost-apply-btn" onClick={apply}
            className="text-[12px] font-semibold px-3 py-1.5 rounded bg-fab-primary text-white hover:opacity-90">Apply</button>
        </div>
      </div>

      <div className="relative my-3">
        <Search className="w-3.5 h-3.5 text-fab-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="cost-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
          placeholder="Search message, buyer or message ID…"
          className="w-full text-[12.5px] pl-9 pr-3 py-2 rounded-lg border border-fab-primary-lighter" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="cost-table">
              <thead>
                <tr className="text-left text-fab-text-muted border-b border-fab-primary-lighter">
                  <th className="py-2 pr-3 font-semibold">Date &amp; Time</th>
                  <th className="py-2 pr-3 font-semibold">Message ID</th>
                  <th className="py-2 pr-3 font-semibold">Buyer / Conversation</th>
                  <th className="py-2 pr-3 font-semibold">Message (Preview)</th>
                  <th className="py-2 pr-3 font-semibold">Processing Type</th>
                  <th className="py-2 pr-3 font-semibold">AI Model</th>
                  <th className="py-2 pr-3 font-semibold text-right">Input Tokens</th>
                  <th className="py-2 pr-3 font-semibold text-right">Output Tokens</th>
                  <th className="py-2 pr-3 font-semibold text-right">Cost (₹)</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody data-testid="cost-table-body">
                {rows.length === 0 ? (
                  <tr><td colSpan={10} className="py-8 text-center text-fab-text-muted italic">No messages in this period.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.message_id} className="border-b border-fab-primary-lighter/60 align-top" data-testid={`cost-row-${r.message_id}`}>
                    <td className="py-2.5 pr-3 whitespace-nowrap">{fmtDate(r.at)}</td>
                    <td className="py-2.5 pr-3 font-mono text-[11px]">{r.message_id?.slice(0, 10) || '—'}</td>
                    <td className="py-2.5 pr-3 font-mono text-[11px] max-w-[140px] truncate" title={r.buyer_key}>{r.buyer_key || '—'}</td>
                    <td className="py-2.5 pr-3 max-w-[220px]"><div className="line-clamp-2">{r.text_preview || '—'}</div></td>
                    <td className="py-2.5 pr-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${PROC_STYLE[r.processing_type] || PROC_STYLE.unknown}`}>
                        {PROC_LABEL[r.processing_type] || r.processing_type}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">{r.model || <span className="text-fab-text-muted">–</span>}</td>
                    <td className="py-2.5 pr-3 text-right font-mono">{r.input_tokens ? r.input_tokens.toLocaleString() : 0}</td>
                    <td className="py-2.5 pr-3 text-right font-mono">{r.output_tokens ? r.output_tokens.toLocaleString() : 0}</td>
                    <td className="py-2.5 pr-3 text-right font-mono font-semibold">{fmtCost(r)}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_STYLE[r.tracking_status] || STATUS_STYLE.unverified}`}>
                        {r.tracking_status === 'tracked' ? 'Tracked' : 'Unverified'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="text-[12px] text-fab-text-muted" data-testid="cost-count-label">
              Showing {totals.total_messages ? from : 0}–{to} of {totals.total_messages || 0} messages
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="cost-prev-page" disabled={(data?.page || 1) <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1.5 rounded border border-fab-primary-lighter disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-[12px] font-semibold px-2">{data?.page || 1} / {data?.total_pages || 1}</span>
              <button data-testid="cost-next-page" disabled={(data?.page || 1) >= (data?.total_pages || 1)}
                onClick={() => setPage((p) => Math.min((data?.total_pages || 1), p + 1))}
                className="p-1.5 rounded border border-fab-primary-lighter disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="text-[13px] font-semibold text-fab-text flex items-center gap-2" data-testid="cost-total">
              Total AI Cost (Selected Period)
              <span className="text-lg font-display font-bold text-fab-primary">₹{Number(totals.verified_cost_inr || 0).toFixed(2)}</span>
            </div>
          </div>
          {totals.unverified_messages > 0 && (
            <div className="text-[11px] text-amber-600 mt-2" data-testid="cost-unverified-note">
              {totals.unverified_messages} message(s) have no verified cost record and are excluded from the total.
            </div>
          )}
        </>
      )}
    </div>
  );
}
